import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import useRasterTile from "../../hooks/useRasterTile";
import MapTemplate from "../../templates/mapTemplate/MapTemplate";
import RiskAPI from "../../apis/riskMap/geojson";
import { Helmet } from "react-helmet-async";
const getCurrentTimeZone = () => {
  const now = new Date();
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Paris",
    timeZoneName: "long",
  })
    .formatToParts(now)
    .find((p) => p.type === "timeZoneName").value;
  return label.includes("Summer") || label.includes("GMT+2") ? "UTC+2" : "UTC+1";
};

const RISK_MAP_COUNTRIES = [{ label: "FR", value: "france" }];

const MapScreen = () => {
  const riskAPI = new RiskAPI();
  const mapRef = useRef(null);
  const [riskMap, setRiskMap] = useState({ show: true });
  const [mapReady, setMapReady] = useState(false);
  const [forecastOffset, setForecastOffset] = useState(0);
  const [riskMapCountry] = useState(RISK_MAP_COUNTRIES[0].value);
  const [departements, setDepartements] = useState({
    data: null,
    show: true,
  });
  const [visibleLevels, setVisibleLevels] = useState([0,1,2,3]);
  const [layerOpacity, setLayerOpacity] = useState(1);

  const currentTimeZone = useMemo(() => getCurrentTimeZone(), []);

  const { dateString, timeString } = useMemo(() => {
    const now = new Date();
    const franceNow = new Date(
      now.toLocaleString("en-US", { timeZone: "Europe/Paris" })
    );
    franceNow.setHours(franceNow.getHours() + forecastOffset);
    const pad = (n) => n.toString().padStart(2, "0");
    return {
      dateString: `${pad(franceNow.getDate())}-${pad(franceNow.getMonth() + 1)}-${franceNow.getFullYear()}`,
      timeString: `${pad(franceNow.getHours())}:00`,
    };
  }, [forecastOffset]);

  // ── Fetch départements GeoJSON ──────────────────────────────────────────
  const getDepartements = useCallback(async () => {
    try {
      const response = await riskAPI.getDepartements();
     if (response?.status === 200) {
        console.log('GeoJSON features:', response.data.features?.length);

        setDepartements((prev) => ({ ...prev, data: response.data }));
      }
    } catch (error) {
      console.error("Error fetching départements GeoJSON:", error);
    } 
  }, []);

  // Fetch once on mount
  useEffect(() => {
    getDepartements();
  }, [getDepartements]);

  // ── Render départements layer on the map ────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;

    const SOURCE_ID = "departements-geojson";
    const LAYER_ID  = "departements-geojson-layer";
    const FILL_LAYER_ID = "departements-geojson-fill";

    const handleStyleLoad = () => {
      const map = mapRef.current;

      if (departements.data && departements.show) {
        // Upsert source
        if (map.getSource(SOURCE_ID)) {
          map.getSource(SOURCE_ID).setData(departements.data);
        } else {
          map.addSource(SOURCE_ID, {
            type: "geojson",
            data: departements.data,
          });
        }

        // Add fill layer for hit-testing (invisible)
        if (!map.getLayer(FILL_LAYER_ID)) {
          map.addLayer({
            id: FILL_LAYER_ID,
            type: "fill",
            source: SOURCE_ID,
            paint: {
              "fill-color": "transparent",
              "fill-opacity": 0,
            },
          });
        }

        // Add line layer (visible borders)
        if (!map.getLayer(LAYER_ID)) {
          map.addLayer({
            id: LAYER_ID,
            type: "line",
            source: SOURCE_ID,
            paint: {
              "line-color": "rgba(28, 252, 252, 1)",
              "line-width": 2,
            },
          });
        }
      } else {
        if (map.getLayer(LAYER_ID))  map.removeLayer(LAYER_ID);
        if (map.getLayer(FILL_LAYER_ID)) map.removeLayer(FILL_LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      }
    };

    const map = mapRef.current;
    if (map.isStyleLoaded()) {
      handleStyleLoad();
    } else {
      map.on("style.load", handleStyleLoad);
    }

    return () => {
      mapRef.current?.off("style.load", handleStyleLoad);
    };
  }, [departements.data, departements.show, mapReady]);

  // ── Double-click popup for départements ─────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || !departements.show || !mapReady) return;

    const map = mapRef.current;
    const LAYER_ID = "departements-geojson-layer";
    const FILL_LAYER_ID = "departements-geojson-fill";

    const handleDblClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: [LAYER_ID, FILL_LAYER_ID],
      });

      if (features.length > 0) {
        const feature = features[0];
        const nom = feature.properties?.nom || "Département";

        new mapboxgl.Popup({
          closeButton: true,
          closeOnClick: true,
          anchor: "bottom",
          offset: 12,
          className: "dept-popup",
        })
          .setLngLat(e.lngLat)
          .setHTML(`
            <div class="dept-popup-content">
              <span class="dept-popup-label">Département</span>
              <span class="dept-popup-name">${nom}</span>
            </div>
          `)
          .addTo(map);
      }
    };

    map.on("dblclick", handleDblClick);

    return () => {
      map.off("dblclick", handleDblClick);
    };
  }, [mapReady, departements.show]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleToggleLevel = useCallback((value) => {
    setVisibleLevels((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value].sort()
    );
  }, []);

  const handleOpacityChange = useCallback((opacity) => {
    setLayerOpacity(opacity);
  }, []);

  const toggleRiskMap = useCallback(() => {
    setRiskMap((prev) => ({ show: !prev.show }));
  }, []);

  const toggleDepartements = useCallback(() => {
    setDepartements((prev) => ({ ...prev, show: !prev.show }));
  }, []);

  const tileActive = riskMap.show && mapReady && visibleLevels.length > 0;

  useRasterTile(mapRef, "riskMap", layerOpacity, tileActive, {
    country: riskMapCountry,
    date: dateString,
    time: timeString,
    utc: currentTimeZone,
    values: visibleLevels,
  });

  return (
    <div style={{ position: "relative", width: "100%", height: "100vh" }}>
        <Helmet>
        <title>Notifications de risque incendie en temps réel | Ensemble pour la Forêt</title>
        <meta
          name="description"
          content="Consultez la carte interactive des risques de feux de forêt en France et activez vos notifications personnalisées par département."
        />
      </Helmet>
      <MapTemplate
        mapRef={mapRef}
        onToggleRiskMap={toggleRiskMap}
        riskMapVisible={riskMap.show}
        onMapLoaded={() => setMapReady(true)}
        forecastOffset={forecastOffset}
        setForecastOffset={setForecastOffset}
        visibleLevels={visibleLevels}
        onToggleLevel={handleToggleLevel}
        layerOpacity={layerOpacity}
        onOpacityChange={handleOpacityChange}
        departements={departements}
        onToggleDepartements={toggleDepartements}
      />
    </div>
  );
};

export default MapScreen;